import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Phone, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BRAND, LOGO_URL } from "@/lib/data";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => navigate("/admin")).catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      toast.success("Sikeres belépés");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Hibás e-mail vagy jelszó");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D11] flex items-center justify-center px-4 grain">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <a href="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6">
          <ArrowLeft className="h-4 w-4" /> Vissza a weboldalra
        </a>
        <Card className="metal-card rounded-2xl p-8">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt={BRAND.name} className="h-16 w-16 rounded-full mb-4" />
            <h1 className="font-display font-bold text-white text-2xl">Admin belépés</h1>
            <p className="text-slate-500 text-sm mt-1">{BRAND.name}</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">E-mail</Label>
              <Input
                data-testid="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@..."
                className="bg-[#0B0D11] border-white/15"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Jelszó</Label>
              <Input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#0B0D11] border-white/15"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full h-12 bg-brand-red hover:bg-[#c11010] text-white font-bold rounded-xl gap-2 glow-red"
            >
              <Lock className="h-4 w-4" /> {loading ? "Belépés..." : "Belépés"}
            </Button>
          </form>
        </Card>
        <a href={BRAND.phoneTel} className="flex items-center justify-center gap-2 text-slate-500 hover:text-brand-gold text-sm mt-6">
          <Phone className="h-4 w-4" /> {BRAND.phoneDisplay}
        </a>
      </motion.div>
    </div>
  );
}
